import React, { useEffect, useMemo, useState } from "react";
import Modal from "./Modal";
import { useData } from "../context/DataContext";
import { formatDateTime, formatKES } from "../utils/format";
import {
  UserPlus,
  UserMinus,
  Trash2,
  Wallet,
  XCircle,
  Pencil,
  Banknote,
  StickyNote,
  MessageSquare,
  PhoneCall,
  ChevronRight,
  RefreshCw,
  Loader2,
} from "lucide-react";
import { formatMonth } from "../utils/format";

const EVENT_META = {
  tenant_created: { icon: UserPlus, label: "Tenant added", tone: "text-indigo-600 bg-indigo-50" },
  tenant_updated: { icon: Pencil, label: "Tenant updated", tone: "text-gray-600 bg-gray-100" },
  tenant_deleted: { icon: Trash2, label: "Tenant deleted", tone: "text-red-600 bg-red-50" },
  tenant_vacated: { icon: UserMinus, label: "Tenant vacated", tone: "text-amber-600 bg-amber-50" },
  payment_recorded: { icon: Wallet, label: "Payment recorded", tone: "text-green-600 bg-green-50" },
  payment_voided: { icon: XCircle, label: "Payment voided", tone: "text-gray-600 bg-gray-100" },
  deposit_collected: { icon: Banknote, label: "Deposit collected", tone: "text-indigo-600 bg-indigo-50" },
  deposit_updated: { icon: Banknote, label: "Deposit updated", tone: "text-indigo-600 bg-indigo-50" },
  tenant_note: { icon: StickyNote, label: "Note added", tone: "text-amber-600 bg-amber-50" },
  sms_sent: { icon: MessageSquare, label: "SMS sent", tone: "text-blue-600 bg-blue-50" },
  ai_call_made: { icon: PhoneCall, label: "AI call made", tone: "text-indigo-600 bg-indigo-50" },
  utility_charge_created: { icon: Wallet, label: "Utility bill entered", tone: "text-blue-600 bg-blue-50" },
  utility_payment_recorded: { icon: Wallet, label: "Utility payment", tone: "text-green-600 bg-green-50" },
  utility_payment_voided: { icon: XCircle, label: "Utility payment voided", tone: "text-gray-600 bg-gray-100" },
};

const CLICKABLE_TYPES = new Set(["sms_sent", "ai_call_made"]);

// scope: "tenant" | "house"
export default function HistoryModal({ open, onClose, scope, tenant }) {
  const { events, communications, refreshCommunications, refreshEvents } = useData();
  const [detail, setDetail] = useState(null);

  // When the modal opens, refetch communications + events so any
  // webhook-driven updates (e.g. AI call transcripts received via the
  // Vapi server URL) become visible without a page reload.
  useEffect(() => {
    if (!open) return;
    refreshCommunications();
    refreshEvents();
  }, [open, refreshCommunications, refreshEvents]);

  const filtered = useMemo(() => {
    if (!tenant) return [];

    // Filter events
    let evts;
    if (scope === "tenant") {
      evts = events.filter((e) => e.tenantId === tenant.id);
    } else {
      evts = events.filter(
        (e) =>
          e.propertyId === tenant.propertyId &&
          String(e.houseNumber || "").toLowerCase() ===
            String(tenant.houseNumber || "").toLowerCase()
      );
    }

    // Merge in communications (SMS + AI calls) as pseudo-events
    const comms = communications
      .filter((c) =>
        scope === "tenant"
          ? c.tenantId === tenant.id
          : c.propertyId === tenant.propertyId &&
            String(c.houseNumber || "").toLowerCase() ===
              String(tenant.houseNumber || "").toLowerCase()
      )
      .map((c) => ({
        ...c,
        type: c.type === "sms" ? "sms_sent" : "ai_call_made",
        at: c.createdAt,
        tenantName: c.tenantName,
        message: c.message || c.transcript || "",
        _comm: c, // keep reference to original communication
      }));

    // Combine and sort by date descending (parse to timestamps for correct ordering)
    return [...evts, ...comms].sort((a, b) => {
      const ta = a.at ? new Date(a.at).getTime() : 0;
      const tb = b.at ? new Date(b.at).getTime() : 0;
      return tb - ta;
    });
  }, [events, communications, scope, tenant]);

  const title =
    scope === "house"
      ? `House ${tenant?.houseNumber} history`
      : `${tenant?.name || "Tenant"} history`;
  const description =
    scope === "house"
      ? "All events for this house unit, across tenants."
      : "All events for this tenant.";

  return (
    <>
      <Modal open={open && !detail} onClose={onClose} title={title} description={description}>
        {filtered.length === 0 ? (
          <div className="py-6 text-center text-sm text-gray-500">No history yet.</div>
        ) : (
          <ol className="relative space-y-3 border-l border-gray-200 dark:border-gray-700 pl-4">
            {filtered.map((e) => {
              const meta = EVENT_META[e.type] || {
                icon: Pencil,
                label: e.type,
                tone: "text-gray-600 bg-gray-100",
              };
              const Icon = meta.icon;
              const clickable = CLICKABLE_TYPES.has(e.type) && e._comm;
              return (
                <li key={e.id} className="relative">
                  <span
                    className={`absolute -left-[26px] flex h-5 w-5 items-center justify-center rounded-full ring-4 ring-white dark:ring-gray-900 ${meta.tone}`}
                  >
                    <Icon className="h-3 w-3" />
                  </span>
                  <div
                    className={`rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 ${
                      clickable ? "cursor-pointer hover:border-indigo-300 dark:hover:border-indigo-700 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors" : ""
                    }`}
                    onClick={clickable ? () => setDetail(e._comm) : undefined}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {meta.label}
                      </span>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDateTime(e.at)}
                        </span>
                        {clickable && <ChevronRight className="h-3.5 w-3.5 text-gray-400" />}
                      </div>
                    </div>
                    <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      {describe(e)}
                    </div>
                    {noteFor(e) && (
                      <div className="mt-1.5 rounded-md bg-gray-50 dark:bg-gray-900 px-2 py-1.5 text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                        <span className="font-medium text-gray-500 dark:text-gray-400">Note: </span>
                        {noteFor(e)}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </Modal>

      {detail && (
        <CommDetailModal
          comm={detail}
          onClose={() => setDetail(null)}
        />
      )}
    </>
  );
}

function CommDetailModal({ comm, onClose }) {
  const isSms = comm.type === "sms";
  const title = isSms ? "SMS details" : "AI call details";
  const { refreshCallTranscript } = useData();
  const [fetching, setFetching] = useState(false);
  const [localComm, setLocalComm] = useState(comm);

  const c = localComm;

  const fetchTranscript = async () => {
    if (!c.id || !c.vapiCallId) return;
    setFetching(true);
    try {
      const updated = await refreshCallTranscript(c.id);
      if (updated) {
        setLocalComm((prev) => ({ ...prev, ...updated }));
      }
    } catch {
      // error handled by api layer
    } finally {
      setFetching(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={title} description={`${c.tenantName} · ${c.phone || ""}`}>
      <div className="space-y-4">
        {/* Status + time */}
        <div className="flex items-center justify-between">
          <CommStatusBadge status={c.status} />
          <span className="text-xs text-gray-500 dark:text-gray-400">{formatDateTime(c.createdAt)}</span>
        </div>

        {/* SMS message */}
        {isSms && c.message && (
          <div>
            <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Message</div>
            <div className="rounded-lg bg-gray-50 dark:bg-gray-900 p-3 text-sm text-gray-900 dark:text-gray-300 whitespace-pre-wrap">
              {c.message}
            </div>
          </div>
        )}

        {/* AI call summary */}
        {!isSms && c.summary && (
          <div>
            <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Summary</div>
            <p className="text-sm text-gray-900 dark:text-white">{c.summary}</p>
          </div>
        )}

        {/* AI call transcript */}
        {!isSms && c.transcript && (
          <div>
            <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Transcript</div>
            <TranscriptBubbles text={c.transcript} />
          </div>
        )}

        {/* AI call duration + cost */}
        {!isSms && c.durationSec != null && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Duration: {Math.floor(c.durationSec / 60)}m {Math.round(c.durationSec % 60)}s
            {c.cost != null && ` · Cost: $${Number(c.cost).toFixed(4)}`}
          </div>
        )}

        {/* No transcript yet — show fetch button */}
        {!isSms && !c.transcript && c.status !== "failed" && c.status !== "skipped" && (
          <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Transcript not yet available.
            </p>
            <button
              onClick={fetchTranscript}
              disabled={fetching}
              className="btn-secondary text-sm"
            >
              {fetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              {fetching ? "Fetching…" : "Fetch transcript"}
            </button>
          </div>
        )}

        {/* Error info for failed */}
        {comm.status === "failed" && (
          <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 px-3 py-2 text-sm text-red-700 dark:text-red-400">
            {isSms ? "SMS failed to deliver." : "Call failed."}{" "}
            {comm.vapiResponse?.error || comm.atResponse?.error || "Please check the phone number."}
          </div>
        )}

        {comm.status === "skipped" && (
          <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
            Call skipped. Not a Safaricom number.
          </div>
        )}

        <div className="flex justify-end pt-2">
          <button className="btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </Modal>
  );
}

const STATUS_STYLES = {
  queued: "bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-400 ring-gray-600/20",
  sent: "bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 ring-green-600/20",
  completed: "bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 ring-green-600/20",
  initiated: "bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-400 ring-blue-600/20",
  in_progress: "bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-400 ring-blue-600/20",
  failed: "bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-400 ring-red-600/20",
  skipped: "bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-400 ring-amber-600/20",
};

function CommStatusBadge({ status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.initiated;
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${style}`}>
      {(status || "unknown").replace("_", " ")}
    </span>
  );
}

function TranscriptBubbles({ text }) {
  if (!text) return null;
  const lines = text.split("\n").filter((l) => l.trim());
  return (
    <div className="space-y-2 rounded-lg bg-gray-50 dark:bg-gray-900 p-3">
      {lines.map((line, i) => {
        const match = line.match(/^(AI|Bot|Assistant|Agent|User|Customer|Human|[^:]{1,30}):\s*(.+)/i);
        if (match) {
          const speaker = match[1].trim();
          const msg = match[2].trim();
          const isAi = /^(ai|bot|assistant|agent)$/i.test(speaker);
          return (
            <div key={i} className={`flex ${isAi ? "justify-start" : "justify-end"}`}>
              <div className={`max-w-[85%] sm:max-w-[75%] rounded-xl px-3 py-2 text-sm ${
                isAi
                  ? "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700"
                  : "bg-indigo-600 text-white"
              }`}>
                <div className={`text-[10px] font-semibold mb-0.5 ${isAi ? "text-gray-500 dark:text-gray-400" : "text-indigo-200"}`}>
                  {speaker}
                </div>
                {msg}
              </div>
            </div>
          );
        }
        return (
          <div key={i} className="text-xs text-gray-500 dark:text-gray-400 text-center italic">{line}</div>
        );
      })}
    </div>
  );
}

function truncate(s, n) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n) + "…" : s;
}

const NOTE_EVENT_TYPES = new Set(["payment_recorded", "utility_payment_recorded"]);

function noteFor(e) {
  if (!NOTE_EVENT_TYPES.has(e.type)) return "";
  const n = (e.note || "").trim();
  return n;
}

function describe(e) {
  switch (e.type) {
    case "payment_recorded": {
      const months = (e.months || []).map(formatMonth).join(", ");
      return `${e.tenantName} · ${formatKES(e.amount)} · ${months || "—"}`;
    }
    case "payment_voided":
      return `${e.tenantName} · ${formatMonth(e.month)} · ${formatKES(e.amount)}`;
    case "deposit_collected":
      return `${e.tenantName} · ${formatKES(e.amount)}`;
    case "deposit_updated":
      return `${e.tenantName} · ${formatKES(e.previousAmount)} → ${formatKES(e.amount)}`;
    case "tenant_note":
      return e.text;
    case "sms_sent":
    case "ai_call_made":
      return e.message ? `${e.tenantName} · ${truncate(e.message, 100)}` : e.tenantName;
    case "utility_charge_created":
      return `${e.tenantName} · ${e.chargeType || "utility"} · ${formatMonth(e.month)} · ${formatKES(e.amount)}`;
    case "utility_payment_recorded":
    case "utility_payment_voided":
      return `${e.tenantName} · ${e.chargeType} · ${formatMonth(e.month)} · ${formatKES(e.amount)}`;
    case "tenant_updated":
      return `${e.tenantName} · fields: ${(e.changes || []).join(", ")}`;
    case "tenant_created":
    case "tenant_vacated":
    case "tenant_deleted":
      return `${e.tenantName} · House ${e.houseNumber}`;
    default:
      return "";
  }
}
